import { useEffect } from "react";
import {
  playExerciseEndFromPayload,
  playSequenceAlarmFromPayload,
  preloadTrainingCueAudio,
  resetTimerAlarmDedupeForNewExercise,
  shouldResetTimerAlarmDedupeFromPayload,
  unlockTrainingCueAudio,
} from "../utils/exerciseTimerSounds";
import { ensureEvalPreCaptureCountdownDuration } from "../utils/evalPreCaptureCountdown";

/**
 * Live Training timer cues for coach + player — driven by server socket events
 * (`sequenceUpdate` / `exerciseStateUpdate` with `alarm`) so every connected
 * client plays start/end sequence and exercise-end sounds in sync.
 */
export function useLiveTrainingPlayerSounds({
  enabled = true,
  socket = null,
} = {}) {
  useEffect(() => {
    if (!enabled) return undefined;
    preloadTrainingCueAudio();
    ensureEvalPreCaptureCountdownDuration();
    const unlock = () => {
      unlockTrainingCueAudio();
    };
    document.addEventListener("pointerdown", unlock, { passive: true });
    document.addEventListener("touchstart", unlock, { passive: true });
    document.addEventListener("keydown", unlock);
    unlock();
    return () => {
      document.removeEventListener("pointerdown", unlock);
      document.removeEventListener("touchstart", unlock);
      document.removeEventListener("keydown", unlock);
    };
  }, [enabled]);

  useEffect(() => {
    if (!enabled || !socket) return undefined;

    const onSequence = (data) => {
      playSequenceAlarmFromPayload(data);
    };
    const onExercise = (data) => {
      if (shouldResetTimerAlarmDedupeFromPayload(data)) {
        resetTimerAlarmDedupeForNewExercise(
          data?.te_id,
          data?.media_sync_at ?? data?.synchronizedStartTime ?? null
        );
      }
      playExerciseEndFromPayload(data);
    };

    socket.on("sequenceUpdate", onSequence);
    socket.on("exerciseStateUpdate", onExercise);
    return () => {
      socket.off("sequenceUpdate", onSequence);
      socket.off("exerciseStateUpdate", onExercise);
    };
  }, [enabled, socket]);
}
